"use client";
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { getArticleExcerpt } from '@/lib/textUtils';

export default function CompanyNewsClient({ articles, totalArticles, categories, currentPage }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');

  const totalPages = Math.ceil(totalArticles / 10);
  const startCount = (currentPage - 1) * 10 + 1;
  const endCount = Math.min(currentPage * 10, totalArticles);

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribeStatus('loading');
    try {
      const response = await fetch('/api/newsletter/subscribers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!response.ok) throw new Error('Subscription failed');
      setEmail('');
      setSubscribeStatus('success');
      setTimeout(() => setSubscribeStatus(''), 5000);
    } catch (err) {
      console.error(err);
      setSubscribeStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-transparent font-sans text-black pt-12 pb-24">
      <div className="max-w-[1281px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-16">
        
        {/* Main Content */}
        <div>
          <div className="mb-12">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-black mb-4 block">COMPANY NEWS</span>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-black mb-6 leading-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
              Official Updates. Stories That Matter.
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Stay informed with the latest announcements, partnerships, and product expansions from the SurgeZirc Media team.
            </p>
          </div>

          <div className="mb-8 text-sm font-medium text-gray-500 uppercase tracking-wider">
            Showing {totalArticles > 0 ? `${startCount}-${endCount}` : '0'} of {totalArticles} articles
          </div>

          <div className="flex flex-col border-t border-gray-200">
            {articles.length > 0 ? (
              articles.map((article) => (
                <article key={article.id} className="py-8 border-b border-gray-200 group">
                  <Link href={`/${article.slug}`}>
                    <h2 className="text-2xl font-bold text-black mb-2 group-hover:underline leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {article.title}
                    </h2>
                    <p className="text-gray-600 mb-3 text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {getArticleExcerpt(article, 180)}
                    </p>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                      {new Date(article.publishedAt || article.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      <span className="mx-2">•</span>
                      {article.category?.name || 'Company News'}
                    </div>
                  </Link>
                </article>
              ))
            ) : (
              <div className="py-12 text-center border-b border-gray-200">
                <p className="text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>No articles found.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-12">
              <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                className="px-6 py-3 bg-white border border-gray-300 text-sm font-bold uppercase tracking-widest hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Previous
              </button>
              <span className="text-sm font-bold text-gray-500">Page {currentPage} of {totalPages}</span>
              <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                className="px-6 py-3 bg-white border border-gray-300 text-sm font-bold uppercase tracking-widest hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="hidden lg:block pt-4">
          
          <div className="mb-12">
            <h3 className="text-xl font-bold border-b-2 border-black pb-3 mb-6 uppercase tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
              News Categories
            </h3>
            <ul className="flex flex-col gap-4">
              <li className="flex justify-between items-center text-sm">
                <Link href="/category/company-news" className="font-bold text-black hover:underline" style={{ fontFamily: "'Inter', sans-serif" }}>
                  All News
                </Link>
                <span className="text-gray-500 font-bold">{totalArticles}</span>
              </li>
              {categories.map((cat) => (
                <li key={cat.id} className="flex justify-between items-center text-sm">
                  <Link href={`/category/company-news/${cat.slug}`} className="text-gray-600 hover:text-black transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {cat.name}
                  </Link>
                  <span className="text-gray-400">{cat.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-8">
            <h3 className="text-lg font-bold text-black mb-3 uppercase tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>Stay Informed</h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Get the latest updates delivered directly to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
              <input 
                type="email" 
                placeholder="Email Address" 
                required 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 bg-white text-sm focus:outline-none focus:border-black transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
              <button 
                type="submit" 
                disabled={subscribeStatus === 'loading'}
                className="w-full bg-black text-white font-bold py-3 px-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:opacity-70"
              >
                {subscribeStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="text-green-600 text-xs mt-3 font-bold text-center" style={{ fontFamily: "'Inter', sans-serif" }}>Thanks for subscribing!</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-red-600 text-xs mt-3 font-bold text-center" style={{ fontFamily: "'Inter', sans-serif" }}>Failed to subscribe.</p>
            )}
          </div>

        </aside>
      </div>
    </div>
  );
}
